import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin, 
  ArrowRight, 
  Heart,
  Users,
  Star,
  MapPin,
  Award,
  Shield
} from 'lucide-react';

const Footer = () => {
  const productLinks = [
    { name: "Features", href: "/#features", type: "section" },
    { name: "Download App", href: "/#download", type: "section" },
    { name: "How It Works", href: "/#how-it-works", type: "section" },
    { name: "For Vendors", href: "/vendors", type: "page" }
  ];

  const resourcesLinks = [
    { name: "Blog", href: "/blog", type: "page" },
    { name: "Support", href: "/contact", type: "page" },
    { name: "Help Center", href: "/contact", type: "page" },
    { name: "Contact", href: "/contact", type: "page" }
  ];

  const companyLinks = [
    { name: "About", href: "/about", type: "page" },
    { name: "Careers", href: "/contact", type: "page" },
    { name: "Partners", href: "/vendors", type: "page" },
    { name: "Contact", href: "/contact", type: "page" }
  ];

  const socialLinks = [
    { icon: Facebook, href: "https://facebook.com/dailzy", label: "Facebook", color: "hover:bg-blue-500" },
    { icon: Twitter, href: "https://twitter.com/dailzy", label: "Twitter", color: "hover:bg-blue-400" },
    { icon: Instagram, href: "https://instagram.com/dailzy", label: "Instagram", color: "hover:bg-pink-500" },
    { icon: Linkedin, href: "https://linkedin.com/company/dailzy", label: "LinkedIn", color: "hover:bg-blue-600" }
  ];

  const handleLinkClick = (link) => {
    if (link.type === "section") {
      const [path, hash] = link.href.split('#');
      if (path === "" || path === "/") {
        // Same page section
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Different page section
        window.location.href = link.href;
      }
    } else if (link.type === "external") {
      // Handle external links or show coming soon
      console.log('Coming soon:', link.name);
    }
  };

  const renderLink = (link, index) => {
    if (link.type === "page") {
      return (
        <li key={index}>
          <Link 
            to={link.href} 
            className="text-gray-300 hover:text-white transition-all duration-300 text-sm md:text-base block group relative py-1 text-center md:text-left"
          >
            <span className="relative z-10 block">{link.name}</span>
            <div className="absolute left-1/2 md:left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 md:translate-x-0 w-0 group-hover:w-1 h-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-full transition-all duration-300 -ml-0 md:-ml-3"></div>
          </Link>
        </li>
      );
    } else {
      return (
        <li key={index}>
          <button 
            onClick={() => handleLinkClick(link)}
            className="text-gray-300 hover:text-white transition-all duration-300 text-sm md:text-base text-center md:text-left w-full group relative py-1"
          >
            <span className="relative z-10 block">{link.name}</span>
            <div className="absolute left-1/2 md:left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 md:translate-x-0 w-0 group-hover:w-1 h-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-full transition-all duration-300 -ml-0 md:-ml-3"></div>
          </button>
        </li>
      );
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4">
        {/* Newsletter */}
        <motion.div 
          className="py-8 md:py-16 border-b border-gray-700"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 lg:gap-12">
            {/* Left side - Title and description */}
            <motion.div 
              className="md:col-span-2 text-center md:text-left"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4 text-white leading-tight">
                Stay Updated with <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">Dailzy</span>
              </h3>
              <p className="text-gray-300 text-sm md:text-base lg:text-lg leading-relaxed">
                Get the latest updates on new features, vendor partnerships, and exclusive offers. 
                <span className="text-orange-400 font-medium"> Join 10K+ subscribers!</span>
              </p>
            </motion.div>
            
            {/* Right side - Email input and button */}
            <motion.div 
              className="md:col-span-3 flex flex-col gap-3 md:gap-4"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col gap-3 md:flex-row md:gap-4 items-stretch md:items-center">
                <div className="relative flex-1">
                  <input 
                    type="email" 
                    placeholder="Enter your email address" 
                    className="w-full px-4 md:px-6 py-3 md:py-4 bg-gray-900/80 border border-gray-700 rounded-xl md:rounded-2xl focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20 text-white placeholder-gray-500 backdrop-blur-sm transition-all duration-300 hover:bg-gray-900 text-sm md:text-base"
                  />
                  <div className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-r from-red-500/10 to-orange-500/10 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </div>
                
                <motion.button 
                  className="bg-gradient-to-r from-red-500 to-orange-500 px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl font-semibold text-white hover:from-red-600 hover:to-orange-600 transition-all duration-300 shadow-lg hover:shadow-red-500/25 flex items-center justify-center group relative overflow-hidden whitespace-nowrap text-sm md:text-base"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative z-10">Subscribe</span>
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
                </motion.button>
              </div>
              
              {/* Privacy notice - below email field */}
              <p className="text-xs md:text-sm text-gray-400 flex items-center justify-center md:justify-start gap-2 mt-1">
                <Shield className="w-3 h-3 md:w-4 md:h-4 text-green-400 flex-shrink-0" />
                <span>We respect your privacy. Unsubscribe at any time.</span>
              </p>
            </motion.div>
          </div>
        </motion.div>
        
        {/* Main content */}
        <div className="py-12 md:py-16 relative">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-500 to-orange-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-orange-500 to-red-500 rounded-full blur-3xl"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 relative z-10">
            
            {/* Brand section */}
            <motion.div
              className="md:col-span-1 text-center md:text-left"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-center md:justify-start gap-3 mb-6">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/25">
                  <img 
                    src="/lovable-uploads/8936458b-f120-42aa-a567-eac1e09a8296.png" 
                    alt="Dailzy" 
                    className="w-7 h-7 md:w-8 md:h-8"
                  />
                </div>
                
                <div className="flex flex-col">
                  <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
                    Dailzy
                  </span>
                  <p className="text-gray-400 text-sm md:text-base font-semibold">
                    Too busy? Do dailzy
                  </p>
                </div>
              </div>
              
              <p className="text-gray-300 text-sm md:text-base mb-6 md:mb-8 leading-relaxed max-w-sm mx-auto md:mx-0">
                Dailzy empowers teams to transform raw data into clear, compelling visuals — making insights easier to share, understand, and act on.
              </p>
              
              {/* Social links */}
              <div className="flex items-center justify-center md:justify-start gap-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 md:w-12 md:h-12 bg-gray-800 hover:bg-gray-700 rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 hover:shadow-lg"
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5 md:w-6 md:h-6" />
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Product */}
            <motion.div
              className="text-center md:text-left"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="mb-6">
                <h3 className="text-lg md:text-xl font-bold mb-1 text-white">Product</h3>
                <div className="w-12 h-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto md:mx-0"></div>
              </div>
              <ul className="space-y-3 md:space-y-4">
                {productLinks.map((link, index) => renderLink(link, index))}
              </ul>
            </motion.div>

            {/* Resources */}
            <motion.div
              className="text-center md:text-left"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="mb-6">
                <h3 className="text-lg md:text-xl font-bold mb-1 text-white">Resources</h3>
                <div className="w-12 h-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto md:mx-0"></div>
              </div>
              <ul className="space-y-3 md:space-y-4">
                {resourcesLinks.map((link, index) => renderLink(link, index))}
              </ul>
            </motion.div>

            {/* Company */}
            <motion.div
              className="text-center md:text-left"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="mb-6">
                <h3 className="text-lg md:text-xl font-bold mb-1 text-white">Company</h3>
                <div className="w-12 h-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto md:mx-0"></div>
              </div>
              <ul className="space-y-3 md:space-y-4">
                {companyLinks.map((link, index) => renderLink(link, index))}
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t border-gray-700/50 py-6 md:py-8 bg-gradient-to-r from-gray-900/50 to-gray-800/30">
          <div className="flex flex-col items-center justify-center md:flex-row md:justify-between gap-4 md:gap-6">
            <motion.div 
              className="order-2 md:order-1"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <p className="text-gray-300 text-sm md:text-base flex items-center justify-center gap-2 font-medium">
                © 2025 Dailzy. Made with <Heart className="w-4 h-4 md:w-5 md:h-5 text-red-500 fill-current animate-pulse" /> in India. All rights reserved.
              </p>
            </motion.div>
            
            <motion.div 
              className="flex items-center gap-6 md:gap-8 order-1 md:order-2"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <button className="text-gray-300 hover:text-white text-sm md:text-base transition-all duration-300 hover:scale-105 relative group">
                <span>Privacy Policy</span>
                <div className="absolute bottom-0 left-0 w-0 group-hover:w-full h-0.5 bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-300"></div>
              </button>
              <button className="text-gray-300 hover:text-white text-sm md:text-base transition-all duration-300 hover:scale-105 relative group">
                <span>Terms of Service</span>
                <div className="absolute bottom-0 left-0 w-0 group-hover:w-full h-0.5 bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-300"></div>
              </button>
              <button className="text-gray-300 hover:text-white text-sm md:text-base transition-all duration-300 hover:scale-105 relative group">
                <span>Cookies Settings</span>
                <div className="absolute bottom-0 left-0 w-0 group-hover:w-full h-0.5 bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-300"></div>
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
